// A browser-side copy of functions/lib/knowledge.js.
//
// The wizard needs to tell someone that their URL is malformed while they are
// still looking at the field, not after a round trip. The server runs the same
// rules again and its answer is the one that counts -- this copy exists purely
// so the form can be helpful, and it must never be more permissive than the
// original or the wizard will cheerfully submit something that gets rejected.
//
// src/lib/knowledge.parity.test.ts loads the real CommonJS module and runs both
// implementations over the same inputs, so the two cannot quietly drift.

import type { KnowledgeSource, QaPair } from "./types";

export const LIMITS = {
  maxSourcesPerAgent: 100,
  maxUrlsPerAgent: 50,
  qaQuestionChars: 1000,
  qaAnswerChars: 8000,
  textChars: 50000,
  maxFileBytes: 20 * 1024 * 1024
} as const;

export const ALLOWED_FILE_EXTENSIONS = [
  "pdf",
  "txt",
  "md",
  "csv",
  "html",
  "htm",
  "docx"
];

export type Check = { ok: true } | { ok: false; error: string };

// Mirrors unsupportedFileMessage in functions/lib/knowledge.js.
export function unsupportedFileMessage(ext: string): string {
  if (ext === "doc") return "Older Word (.doc) files can't be read. Open it in Word and save it as .docx or PDF, then upload that.";
  return `.${ext} files can't be indexed (allowed: ${ALLOWED_FILE_EXTENSIONS.join(", ")})`;
}

// How big an uploaded file may be, by type. Mirrors functions/lib/knowledge.js
// (checked in parity.test.ts).
const MB = 1024 * 1024;
export const FILE_MAX_MB: Record<string, number> = { pdf: 20, docx: 10, txt: 2, md: 2, csv: 2, html: 2, htm: 2 };

export function maxFileBytes(ext: string): number {
  return (FILE_MAX_MB[ext] || 2) * MB;
}

function megabytes(bytes: unknown): string {
  return `${(Number(bytes) / MB).toFixed(1).replace(/\.0$/, "")} MB`;
}

export function fileTooBigMessage({ fileName, ext, sizeBytes }: { fileName: unknown; ext: string; sizeBytes: unknown }): string {
  const name = `"${String(fileName)}" is ${megabytes(sizeBytes)}.`;
  if (ext === "pdf") {
    return `${name} PDFs can be up to ${FILE_MAX_MB.pdf} MB. Save a smaller copy (Reduce File Size in Acrobat or Preview), or split it into parts and upload each one.`;
  }
  if (ext === "docx") {
    return `${name} Word files can be up to ${FILE_MAX_MB.docx} MB, and big ones are usually big because of pictures. Save it as a PDF, or take the pictures out, then upload it again.`;
  }
  return `${name} Text, Markdown, CSV and HTML files can be up to ${FILE_MAX_MB[ext] || 2} MB. Split it into smaller files and upload each one.`;
}

export function fileExtension(name: unknown): string {
  const match = /\.([a-z0-9]+)$/i.exec(String(name ?? "").trim());
  return match ? match[1].toLowerCase() : "";
}

const PRIVATE_HOST =
  /^(localhost|127\.|10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.|0\.|169\.254\.|\[?::1\]?)/i;

export function validateUrl(raw: unknown): Check {
  if (typeof raw !== "string" || !raw.trim()) return { ok: false, error: "URL is empty" };

  let url: URL;
  try {
    url = new URL(raw.trim());
  } catch {
    return { ok: false, error: `"${raw}" is not a valid URL` };
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") {
    return { ok: false, error: "Only http and https URLs can be indexed" };
  }
  if (PRIVATE_HOST.test(url.hostname)) {
    return { ok: false, error: "We can't read private or local addresses" };
  }
  return { ok: true };
}

export function validateQaPair(pair: Partial<QaPair> | undefined): Check {
  const question = typeof pair?.question === "string" ? pair.question.trim() : "";
  const answer = typeof pair?.answer === "string" ? pair.answer.trim() : "";

  if (!question) return { ok: false, error: "Question is empty" };
  if (!answer) return { ok: false, error: `"${question.slice(0, 40)}" has no answer` };
  if (question.length > LIMITS.qaQuestionChars) {
    return { ok: false, error: `Question is over ${LIMITS.qaQuestionChars} characters` };
  }
  if (answer.length > LIMITS.qaAnswerChars) {
    return { ok: false, error: `Answer is over ${LIMITS.qaAnswerChars} characters` };
  }
  return { ok: true };
}

export function validateSource(source: Partial<KnowledgeSource> | undefined): Check {
  switch (source?.type) {
    case "url":
      return validateUrl((source as { url?: unknown }).url);

    case "qa": {
      const pairs = (source as { pairs?: unknown }).pairs;
      if (!Array.isArray(pairs) || pairs.length === 0) {
        return { ok: false, error: "Add at least one question and answer" };
      }
      for (const pair of pairs) {
        const result = validateQaPair(pair);
        if (!result.ok) return result;
      }
      return { ok: true };
    }

    case "text": {
      const raw = (source as { text?: unknown }).text;
      const text = typeof raw === "string" ? raw.trim() : "";
      if (!text) return { ok: false, error: "Text is empty" };
      if (text.length > LIMITS.textChars) {
        return { ok: false, error: `Text is over ${LIMITS.textChars} characters` };
      }
      return { ok: true };
    }

    case "file": {
      const file = source as { fileName?: unknown; storagePath?: unknown; sizeBytes?: unknown };
      const ext = fileExtension(file.fileName);
      if (!ext) return { ok: false, error: "File has no extension" };
      if (!ALLOWED_FILE_EXTENSIONS.includes(ext)) {
        return {
          ok: false,
          error: unsupportedFileMessage(ext)
        };
      }
      if (!file.storagePath) return { ok: false, error: "File was not uploaded" };
      if (Number(file.sizeBytes) > maxFileBytes(ext)) {
        return { ok: false, error: fileTooBigMessage({ fileName: file.fileName, ext, sizeBytes: file.sizeBytes }) };
      }
      return { ok: true };
    }

    default:
      return { ok: false, error: `Unknown source type "${source?.type}"` };
  }
}

export function validateSources(sources: unknown): { ok: boolean; errors: string[] } {
  if (!Array.isArray(sources) || sources.length === 0) {
    return { ok: false, errors: ["Add at least one knowledge source so the bot has something to say"] };
  }
  if (sources.length > LIMITS.maxSourcesPerAgent) {
    return { ok: false, errors: [`Too many sources (max ${LIMITS.maxSourcesPerAgent})`] };
  }

  const urlCount = sources.filter((s) => s?.type === "url").length;
  if (urlCount > LIMITS.maxUrlsPerAgent) {
    return { ok: false, errors: [`Too many URLs (max ${LIMITS.maxUrlsPerAgent})`] };
  }

  const errors: string[] = [];
  sources.forEach((source, index) => {
    const result = validateSource(source);
    if (!result.ok) errors.push(`Source ${index + 1}: ${result.error}`);
  });

  return errors.length ? { ok: false, errors } : { ok: true, errors: [] };
}

// What the file picker should offer. Built from the same list the server
// checks, so adding an extension in one place cannot leave the picker behind.
export const FILE_ACCEPT = ALLOWED_FILE_EXTENSIONS.map((ext) => `.${ext}`).join(",");
